import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Search, SlidersHorizontal } from "lucide-react";
import { DealCard } from "@/pages/Home";

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  const [q, setQ] = useState(searchParams.get("q") || "");
  const [zip, setZip] = useState(searchParams.get("zip_code") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "all");
  const [priceRange, setPriceRange] = useState([0, 300]);
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get("/deals/categories").then(r => setCategories(r.data.categories));
  }, []);

  const search = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (zip) params.set("zip_code", zip);
    if (category && category !== "all") params.set("category", category);
    if (priceRange[0] > 0) params.set("min_price", priceRange[0]);
    if (priceRange[1] < 300) params.set("max_price", priceRange[1]);
    if (sort) params.set("sort", sort);
    setSearchParams(params);
    try {
      const { data } = await api.get(`/deals?${params.toString()}`);
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { search(); /* eslint-disable-next-line */ }, [category, sort]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">Marketplace</div>
        <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl tracking-tight">Browse massage deals</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
        <aside className="space-y-6">
          <div className="bg-white p-6 rounded-2xl border border-[#E8E2D6]">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60] mb-4 flex items-center gap-2"><SlidersHorizontal className="w-3.5 h-3.5" strokeWidth={1.5}/> Filters</div>

            <label className="block text-sm font-medium mb-2">Keyword</label>
            <Input data-testid="filter-keyword" value={q} onChange={e=>setQ(e.target.value)} placeholder="e.g. couples" className="bg-[#F9F7F3] border-[#E8E2D6] rounded-xl" />

            <label className="block text-sm font-medium mt-5 mb-2">ZIP code</label>
            <Input data-testid="filter-zip" value={zip} onChange={e=>setZip(e.target.value)} placeholder="94107" className="bg-[#F9F7F3] border-[#E8E2D6] rounded-xl" />

            <label className="block text-sm font-medium mt-5 mb-2">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="filter-category" className="bg-[#F9F7F3] border-[#E8E2D6] rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <label className="block text-sm font-medium mt-5 mb-2">Price range: ${priceRange[0]} – ${priceRange[1]}</label>
            <Slider data-testid="filter-price-range" value={priceRange} onValueChange={setPriceRange} min={0} max={300} step={5} className="mt-3" />

            <Button data-testid="filter-apply-button" onClick={search} className="w-full mt-6 rounded-full bg-[#D97757] hover:bg-[#C26243] text-white">
              <Search className="w-4 h-4 mr-2" /> Apply filters
            </Button>
          </div>
        </aside>

        <section>
          <div className="flex items-center justify-between mb-6">
            <div className="text-sm text-[#5F6C60]" data-testid="results-count">{loading ? "Searching…" : `${items.length} deal${items.length===1?'':'s'} found`}</div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="filter-sort" className="w-44 bg-white border-[#E8E2D6] rounded-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price_asc">Price · Low to high</SelectItem>
                <SelectItem value="price_desc">Price · High to low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({length:6}).map((_,i)=>(
                <div key={i} className="h-80 rounded-2xl bg-white border border-[#E8E2D6] animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[#E8E2D6] p-16 text-center">
              <p className="font-serif-display text-2xl">No deals match your filters</p>
              <p className="text-[#5F6C60] mt-2">Try widening your search or browsing all categories.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map(d => <DealCard key={d.id} deal={d} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
